import 'app/sentry';
import { createSSRApp, reactive } from 'vue';
import { devOnly } from 'util/devOnly';
import 'app/actionLogger';
import { heartbeat } from 'app/heartbeat';
import { enableExternalScripts } from 'util/enableExternalScripts';
import { performanceMark } from 'util/performanceMark';
import { refreshToken } from 'util/auth';
import { hasConsentGrantedOrDenied } from 'util/consent';
import { onFirstUserInteraction } from 'util/onFirstInteraction';
import * as Sentry from '@sentry/browser';
import { onDomContentLoaded } from 'util/onDomReady';

const appFactory = (component, props) => createSSRApp(component, props);

/**
 * Sets up the store with initial state.
 *
 * @param {Object} app - The Vue application instance.
 * @param {Object} props - The reactive props object.
 */
async function setupStoreForDebugging(app, props) {
    const { createPinia } = await import('pinia');
    const { useStore } = await import('../../stores/app-store');
    const pinia = createPinia();
    app.use(pinia);
    const store = useStore();
    store.setInitialState(props);
}

let hadPageActiveEvent = false;
function onPageActive() {
    if (hadPageActiveEvent) return;

    performanceMark('pageActive');
    hadPageActiveEvent = true;
}

export function initializeFrontend(component, options = {}) {
    const {
        initialState = window.VUE_INITIAL_STATE,
        immediately = false,
        rootContainer = '#app',
    } = options;

    function mountVueApp() {
        try {
            // eslint-disable-next-line no-console
            devOnly(() => console.time('Vue app has been created and mounted in'));

            const props = reactive(initialState);
            const app = appFactory(component, props);
            app.mount(rootContainer, true);

            devOnly(() => {
                // eslint-disable-next-line no-console
                console.timeEnd('Vue app has been created and mounted in');
                setupStoreForDebugging(app, props);
            });
        } finally {
            onPageActive();
        }
    }

    let hasSSRError;
    try {
        const { statusCode } = JSON.parse(initialState.vueHtml);
        hasSSRError = !String(statusCode).startsWith('2');
        if (hasSSRError) {
            Sentry.captureException('SSR failed with non-2xx code', {
                tags: {
                    route: window.location.pathname,
                },
                contexts: {
                    ssrResult: initialState.vueHtml,
                },
            });
        }
    } catch {
        hasSSRError = false;
    }

    const shouldMountInstantly = immediately
        || hasSSRError // gracefully recover from SSR errors by mounting client-side ASAP
        || new URLSearchParams(document.location.search).get('forceInitialize'); // Used by critical CSS

    delete window.VUE_INITIAL_STATE?.vueHtml;

    if (shouldMountInstantly) {
        mountVueApp();
    } else {
        onFirstUserInteraction(mountVueApp);
    }

    setTimeout(onPageActive, 1000);

    onDomContentLoaded(() => {
        performanceMark('JS + DOM done loading');
        if (hasConsentGrantedOrDenied()) {
            enableExternalScripts();
        } else {
            onFirstUserInteraction(enableExternalScripts);
        }
    });

    // Setup Heartbeat error handling
    heartbeat.events.on('error', (e) => {
        if (e.arguments[0].error.status === 401) {
            refreshToken();
        }
    });
}
